
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useCashbackToggle } from "@/hooks/useCashbackToggle";
import { useCashbackRules } from "@/hooks/useCashbackRules";
import { useOnboarding } from "../OnboardingProvider";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";

interface CashbackStepProps {
  onNext: () => void;
  onPrev: () => void;
}

export function CashbackStep({ onNext, onPrev }: CashbackStepProps) {
  const { completeStep } = useOnboarding();
  const { updateProgress } = useOnboardingProgress();
  const { toggleCashback, isToggling } = useCashbackToggle();
  const { formData, updateFormField, saveRule, isSaving } = useCashbackRules();
  const { toast } = useToast();
  const [cashbackEnabled, setCashbackEnabled] = useState(true);

  const handleNext = async () => {
    try {
      if (cashbackEnabled) {
        // Salvar as regras de cashback
        await saveRule();
        // Ativar o cashback
        await toggleCashback(true);
      }

      // Marcar step como completo e atualizar progresso
      completeStep('cashback');
      await updateProgress({ cashback_configured: true });

      toast({
        title: "Cashback configurado!",
        description: cashbackEnabled ? 
          "Seu sistema de cashback está ativo e funcionando." : 
          "Você pode ativar o cashback mais tarde nas configurações.",
      });

      onNext();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível configurar o cashback",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Configure seu Cashback</h2>
        <p className="text-muted-foreground">
          Defina as regras básicas do seu programa de cashback.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Ativar Cashback</CardTitle>
              <CardDescription>
                Comece oferecendo cashback aos seus clientes
              </CardDescription>
            </div>
            <Switch
              checked={cashbackEnabled}
              onCheckedChange={setCashbackEnabled}
              disabled={isToggling}
            />
          </div>
        </CardHeader>

        {cashbackEnabled && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="percentage">Percentual (%)</Label>
                <Input
                  id="percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.percentage}
                  onChange={(e) => updateFormField('percentage', e.target.value)}
                  placeholder="5.00"
                />
              </div>

              <div>
                <Label htmlFor="multiplier">Multiplicador</Label>
                <Input
                  id="multiplier"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.multiplier}
                  onChange={(e) => updateFormField('multiplier', e.target.value)}
                  placeholder="3"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="expiration">Dias para expirar</Label>
              <Input
                id="expiration"
                type="number"
                min="1"
                max="365"
                value={formData.expiration_days}
                onChange={(e) => updateFormField('expiration_days', e.target.value)}
                placeholder="30"
              />
            </div>

            <div className="bg-muted p-3 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor da compra:</span>
                  <span className="font-medium">R$ 100,00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cashback ({formData.percentage || "5"}%):</span>
                  <span className="text-primary">R$ {(100 * (parseFloat(formData.percentage || "5") / 100)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Multiplicador:</span>
                  <span>x{formData.multiplier || "3"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gasto Mínimo:</span>
                  <span className="font-medium">R$ {(100 * (parseFloat(formData.percentage || "5") / 100) * parseInt(formData.multiplier || "3")).toFixed(2)}</span>
                </div>
                <hr className="border-border" />
                <div className="flex justify-between font-medium">
                  <span>Métrica de retorno:</span>
                  <span className="text-primary">{(((100 * (parseFloat(formData.percentage || "5") / 100) * parseInt(formData.multiplier || "3")) - (100 * (parseFloat(formData.percentage || "5") / 100))) / (100 * (parseFloat(formData.percentage || "5") / 100)) * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} className="flex-1">
          Voltar
        </Button>
        <Button 
          onClick={handleNext} 
          disabled={isToggling || isSaving}
          className="flex-1"
        >
          {isToggling || isSaving ? "Salvando..." : "Continuar"}
        </Button>
      </div>
    </div>
  );
}
